/**
 * Shared types for the Lapo core. Framework-agnostic; safe to import on client and server.
 */

/** A page is either a list-first journal or a prose-first document (architecture §3). */
export enum PageType {
  Journal = 'journal',
  Document = 'document',
}

/** Entity kinds referenced by [[ ]] and used to route the morning brief (architecture §4). */
export enum EntityType {
  Person = 'person',
  Company = 'company',
  Deal = 'deal',
  Topic = 'topic',
  /** A long-running initiative (area or program) — see the Initiatives module. */
  Initiative = 'initiative',
}

/** Skill execution policy (architecture §7 / IMPL-08). */
export enum SkillPolicy {
  Auto = 'auto',
  Confirm = 'confirm',
  Gated = 'gated',
}

/**
 * FACET — the entity types a page can be (typed-note-properties.md P1). Initiatives are excluded
 * (they have their own store/intelligence); journals are never typed.
 */
export type NoteEntityType = 'person' | 'company' | 'deal' | 'topic';

/** FACET — one property value. Dates are ISO strings; entity-links are entityId strings (P3). */
export type PropertyValue = string | number | null;

/** FACET P7 — per-field provenance: a `user` write is permanently off-limits to auto-fill. */
export interface PropertyProvenance {
  source: 'user' | 'auto';
  updatedAt: number;
}

/** Document-level attributes carried on the doc node so they never clutter the body. */
export interface DocAttributes {
  pageType: PageType;
  /** emoji or "material:icon_name" */
  icon: string | null;
  /** banner image url or null */
  banner: string | null;
  /** vertical focal point for the banner image, e.g. "20%" (Logseq `banner-align`); null = center */
  bannerAlign: string | null;
  /** horizontal focal point for the banner image, e.g. "30%"; null = center */
  bannerAlignX: string | null;
  /** banner zoom as a background-size width percent (100 = fit width); null = default (100) */
  bannerZoom: number | null;
  /** banner height in px; null = default (220) */
  bannerHeight: number | null;
  /** page-level tags (kept out of the body) */
  tags: string[];
  /**
   * Alternate names for this page. A `[[alias]]` reference resolves to this page's entity, so a
   * page titled "Michael Connelly" with alias "Connelly" collects backlinks from both spellings.
   */
  aliases: string[];
  // --- FACET — typed structured properties (typed-note-properties.md) ----------------------------
  /** The page's entity type; null/absent = untyped (behavior identical to pre-FACET). */
  entityType?: NoteEntityType | null;
  /** Schema-keyed values (see core `PROPERTY_SCHEMAS`); unknown keys are stripped on validate. */
  properties?: Record<string, PropertyValue> | null;
  /** Per-field provenance (P7 user-wins invariant); keys mirror `properties`. */
  propertyMeta?: Record<string, PropertyProvenance> | null;
}

/** Lightweight metadata for the local document index / list view (IMPL-02). */
export interface DocMeta {
  noteId: string;
  userId: string;
  type: PageType;
  title: string;
  icon: string | null;
  tags: string[];
  /**
   * Alternate names for this page (from `DocAttributes.aliases`). Carried here so the Cmd+K switcher
   * can match and badge alias rows without opening the doc (FIND-01/FIND-02). Empty when unset.
   */
  aliases: string[];
  updatedAt: number;
}

/**
 * A page preset selects defaults, keymap, and chrome for a PageType.
 * It does NOT fork the editor (architecture §3) — presets implement this shared shape.
 */
export interface PagePreset {
  type: PageType;
  /** node name used as the default block for a new empty page */
  defaultBlock: string;
  /** human label for chrome */
  label: string;
  /**
   * keymap entries as command ids; concrete bindings are wired in the ui package.
   * Kept as data here so core stays DOM-free and testable.
   */
  keymap: Record<string, string>;
  /** whether a raw-markdown source mode is offered (journal only; architecture §3) */
  rawMarkdownSource: boolean;
}

/** Result of serializing a document to markdown, including round-trip fidelity info. */
export interface SerializeResult {
  markdown: string;
  /** node types that could not round-trip cleanly and were serialized to a fallback */
  lossyNodes: string[];
}

/** A reference extracted from a document body (wikilink or tag) — used by the indexer. */
export interface ExtractedRef {
  kind: 'wikilink' | 'tag';
  /** for wikilink: the target entity name; for tag: the name (without # / +) */
  value: string;
  /** display label for wikilinks ([[target|label]]) */
  label?: string;
  /** surrounding text context for the backlink edge */
  context: string;
  /**
   * The stable id of the enclosing addressable block (paragraph/heading), when it has one. Lets a
   * backlink scroll to the exact source line, and gives the Initiatives triage sidecar a stable key
   * that survives reindex (which regenerates edge ids). Undefined for non-addressable blocks.
   */
  blockId?: string;
}
